import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Stack } from '@mui/material';

import { GenerateWarehouseDocumentTypeRequestEnum } from '../../../../../core/enums/inventory/warehouse_document';
import { createWarehouseDocumentAction, createWarehouseDocumentState } from '../../../../../features/inventory/create_warehouse_document';
import useNotification from '../../../../../core/hooks/use_notification';
import ThreeLevelSteps from '../../../common/steps';
import SummaryList from '../../../common/warehouse_and_food_partner_order_form/summary_list';
import { createWarehouseDocumentNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { ROUTE_WAREHOUSE_DOCUMENTS } from '../../../../../core/constants';
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';
import { Button, SubmitButton } from '../../../../common/button';
import ConfirmDialog from '../../../common/confirm_dialog';

const Summary = ({ setView, data }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const createWarehouseDocument = useSelector(createWarehouseDocumentState);

    const onReturn = () => {
        if (data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.PZ_DOCUMENT.value) {
            setView(1);
        }
        if (data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.PZ_AND_MM_DOCUMENTS.value) {
            setView(2);
        }
        if (data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value) {
            setView(3);
        }
        if (data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.PW_DOCUMENT.value) {
            setView(4);
        }
    };

    const onSave = (generateLabels) => {
        onSubmit(createWarehouseDocumentAction, { ...data?.form, generateLabels });
    };

    const onSubmit = (action, form) => {
        dispatch(action(form))
            .then(response => {
                showNotification(createWarehouseDocumentNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(ROUTE_WAREHOUSE_DOCUMENTS);
            });
    };

    const getSummaryList = () => {
        const documentType = data?.form?.type;
        const allProducts = data?.formik?.productsList.flat();
        const types = [...new Set(allProducts?.map(product => product?.type))];
        const productsByType = types?.map(type => [...allProducts?.filter(product => product?.type === type)]);
        const products = productsByType?.map((products, index) => {
            return [...new Set(productsByType[index]?.map(product => product?.name))]?.map(productName => {
                const productQuantity = products?.filter(product => product?.name === productName)
                    ?.map(product => product?.quantity)
                    ?.reduce((accumulator, current) => accumulator + current, 0);

                return {
                    name: productName,
                    quantity: productQuantity
                };
            });
        });
        const destinationProducts = types?.map(type => {
            return data?.formik?.destinationsList?.map((_, index) => {
                const productsByType = data?.formik?.productsList[index]?.filter(product => product?.type === type);

                return [...new Set(productsByType?.map(product => product?.name))]?.map(productName => {
                    const productQuantity = productsByType?.filter(product => product?.name === productName)
                        ?.map(product => product?.quantity)
                        ?.reduce((accumulator, current) => accumulator + current, 0);

                    return {
                        name: productName,
                        quantity: productQuantity
                    };
                });
            });
        });

        return (
            <SummaryList
                showDestinationProductsDialog={ ![GenerateWarehouseDocumentTypeRequestEnum.PZ_DOCUMENT.value, GenerateWarehouseDocumentTypeRequestEnum.PW_DOCUMENT.value].includes(documentType) }
                types={ types }
                productsByType={ productsByType }
                products={ products }
                destinationsList={ data?.formik?.destinationsList }
                destinationProducts={ destinationProducts }
            />
        );
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
            <Box sx={ { flexGrow: 1, pb: 4 } }>
                <Stack spacing={ 3 }>
                    <ThreeLevelSteps thirdStep/>

                    <SectionPaper
                        title="Podsumowanie"
                        description={ data?.form?.locationDivision ? `Wybranych lokalizacji docelowych: ${ data?.formik?.destinationsList?.length || 0 }` : undefined }
                    >
                        <Box sx={ { mt: 2 } }>
                            { getSummaryList() }
                        </Box>
                    </SectionPaper>
                </Stack>
            </Box>

            <BottomActionBar>
                <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                    <Button variant="outlined" onClick={ onReturn }>Wróć</Button>
                    <Stack direction="row" spacing={ 2 }>
                        {
                            (data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value || data?.form?.type === GenerateWarehouseDocumentTypeRequestEnum.PW_DOCUMENT.value)
                                ? <SubmitButton type="button" isLoading={ createWarehouseDocument?.loading }
                                                onClick={ () => onSave(false) }>Zapisz</SubmitButton>
                                : <ConfirmDialog
                                    isLoading={ createWarehouseDocument?.loading }
                                    title="Wygenerować paczkę naklejek?"
                                    secondaryAction={ () => onSave(false) }
                                    secondaryText="Pomiń"
                                    primaryAction={ () => onSave(true) }
                                    primaryText="Wygeneruj"
                                    displayIcon={ false }
                                    buttonText="Zapisz"
                                />
                        }
                    </Stack>
                </Box>
            </BottomActionBar>
        </Box>
    );
};

export default Summary;